import type { Cart } from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import { SignUpModal } from '@components/Account/SignUpModal';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { type BUTTON_VARIANT_STYLES, Button } from '@components/Button/Button';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { Icons } from '@components/Icons/Icons';
import { LoginModalUncontrolled } from '@components/LoginForm/LoginModal';
import { TooltipWrapper } from '@components/Tooltip/Tooltip';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { useAddItems } from '@hooks/cart/useAddItems';
import { useCustomer } from '@hooks/customer/useCustomer';
import { logBreadcrumb } from '@lib/logs';
import type React from 'react';
import { type ReactElement, type ReactNode, useContext, useState } from 'react';
import { twJoin } from 'tailwind-merge';

export enum AddToCartStatus {
  None = 0,
  Adding = 1,
  Added = 2,
  Failed = 3,
}

interface Props {
  queryID?: string[] | string;
  disabled?: boolean;
  displayIcon?: boolean;
  productId: number;
  variantId: number;
  quantity: number;
  sku: string;
  name: string;
  triggerCartModal?: boolean;
  successCallback?: () => void;
  className?: string;
  children?: ReactNode;
  tooltipText?: string;
  buttonStyle?: keyof typeof BUTTON_VARIANT_STYLES;
}

export function AddToCart({
  queryID,
  disabled,
  displayIcon = true,
  productId,
  variantId,
  sku,
  name,
  quantity = 1,
  triggerCartModal = true,
  className,
  successCallback,
  customIcon,
  tooltipText = '',
  buttonStyle,
  ...props
}: Props & Record<string, any>): ReactElement {
  const addItem = useAddItems({
    include: ['line_items.physical_items.options'],
  });
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>();
  const { setQuickCartIsOpen, addingToCartStatus, setAddingToCartStatus } =
    useContext<UIContextInterface>(UIContext);

  // Add the item
  const addToCart = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    logBreadcrumb({
      category: 'AddToCart',
      message: 'Clicked add to cart button',
      level: 'info',
      data: {
        productId,
        variantId,
        quantity,
      },
    });

    let cartResponse: { data: Cart | null; error: string | null };
    try {
      setAddingToCartStatus(AddToCartStatus.Adding);
      cartResponse = await addItem({
        lineItems: [
          {
            productId,
            variantId,
            quantity,
          },
        ],
      });

      if (cartResponse.error !== null) {
        setError(true);
        if (cartResponse.error) {
          setErrorMessage(
            <Paragraph>
              We couldn't add that to your cart. {cartResponse.error}
            </Paragraph>
          );
          setAddingToCartStatus(AddToCartStatus.Failed);
        } else {
          throw new Error('Unexpected error');
        }
      } else {
        if (triggerCartModal) setQuickCartIsOpen(true);
        setAddingToCartStatus(AddToCartStatus.Added);
        if (successCallback) successCallback();
      }
    } catch (error) {
      setAddingToCartStatus(AddToCartStatus.Failed);
      setError(true);
      setErrorMessage(
        <Paragraph>
          We couldn't add that to your cart. If you continue to have issues,
          please contact customer service.
        </Paragraph>
      );
    }
  };

  return (
    <>
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
      <form onSubmit={addToCart}>
        <input type="hidden" value={productId} name="productId" />
        <input type="hidden" value={variantId} name="variantId" />
        <input type="hidden" value={quantity} name="quantity" />
        <input type="hidden" value={sku} name="sku" />
        <input type="hidden" value={name} name="productName" />
        <TooltipWrapper text={tooltipText}>
          <Button
            buttonStyle={buttonStyle ?? 'primary'}
            className={twJoin([className ?? '', 'gae-product-atc'])}
            type="submit"
            disabled={addingToCartStatus === AddToCartStatus.Adding || disabled}
            icon={customIcon ?? <Icons.CartPlus />}
            data-umami-event="hue-add-to-cart-clicked"
            data-umami-event-productid={productId}
            data-umami-event-variantid={variantId}
            data-umami-event-quantity={quantity}
            data-umami-event-sku={sku}
            {...props}
          >
            {!customIcon && 'Add to cart'}
          </Button>
        </TooltipWrapper>
      </form>
    </>
  );
}

export function AddToCartAccountRequired({
  queryID,
  disabled,
  displayIcon = true,
  productId,
  variantId,
  sku,
  name,
  quantity = 1,
  triggerCartModal = true,
  className,
  successCallback,
  customIcon,
  tooltipText = '',
  ...props
}: Props & Record<string, any>): ReactElement {
  const { customer } = useCustomer();
  const addItem = useAddItems({
    include: ['line_items.physical_items.options'],
  });

  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>();

  const { setQuickCartIsOpen, addingToCartStatus, setAddingToCartStatus } =
    useContext<UIContextInterface>(UIContext);

  // Add the item
  const addToCart = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    logBreadcrumb({
      category: 'AddToCart',
      message: 'Clicked add to cart button',
      level: 'info',
      data: {
        productId,
        variantId,
        quantity,
      },
    });

    // if not signed in, prompt to create account and prefill form with selected sample
    if (!customer) {
      setShowSignUpModal(true);
      return;
    }

    let cartResponse: { data: Cart | null; error: string | null };
    try {
      setAddingToCartStatus(AddToCartStatus.Adding);
      cartResponse = await addItem({
        lineItems: [
          {
            productId,
            variantId,
            quantity,
          },
        ],
      });

      if (cartResponse.error !== null) {
        setError(true);
        if (cartResponse.error) {
          setAddingToCartStatus(AddToCartStatus.Failed);
          setErrorMessage(
            <Paragraph>
              We couldn't add that to your cart. {cartResponse.error}
            </Paragraph>
          );
        } else {
          throw new Error('Unexpected error');
        }
      } else {
        if (triggerCartModal) {
          setQuickCartIsOpen(true);
        }
      }
      setAddingToCartStatus(AddToCartStatus.Added);
      if (successCallback) successCallback();
    } catch (error) {
      setAddingToCartStatus(AddToCartStatus.Failed);
      setError(true);
      setErrorMessage(
        <Paragraph>
          We couldn't add that to your cart. If you continue to have issues,
          please contact customer service.
        </Paragraph>
      );
    }
  };

  return (
    <>
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
      <SignUpModal
        showModal={showSignUpModal}
        setShowModal={setShowSignUpModal}
        leadCopy={
          <>
            You'll need to create an account or{' '}
            <LoginModalUncontrolled
              trigger={
                <Button buttonStyle="text" className="inline">
                  login
                </Button>
              }
              title={<>Login to your account</>}
            />{' '}
            to add this to your cart, but don't worry, it's quick and easy!
          </>
        }
        successMessage={
          <p className="dark:text-white">
            Welcome aboard! You're all signed up and should receive an email to
            set your password. In the meantime, you can now add that to cart 🥳
          </p>
        }
      />
      <form onSubmit={addToCart} className="mb-4">
        <input type="hidden" value={productId} name="productId" />
        <input type="hidden" value={variantId} name="variantId" />
        <input type="hidden" value={quantity} name="quantity" />
        <input type="hidden" value={sku} name="sku" />
        <input type="hidden" value={name} name="productName" />
        <TooltipWrapper text={tooltipText}>
          <Button
            buttonStyle="primary"
            className={twJoin([className ?? '', 'gae-product-atc'])}
            type="submit"
            disabled={addingToCartStatus === AddToCartStatus.Adding || disabled}
            icon={customIcon ?? <Icons.CartPlus />}
            data-umami-event="hue-add-to-cart-clicked"
            data-umami-event-productid={productId}
            data-umami-event-variantid={variantId}
            data-umami-event-quantity={quantity}
            data-umami-event-sku={sku}
            {...props}
          >
            {!customIcon && 'Add to cart'}
          </Button>
        </TooltipWrapper>
      </form>
    </>
  );
}

export function AddToCartCustomChildren({
  queryID,
  disabled,
  productId,
  variantId,
  buttonStyle,
  sku,
  name,
  quantity = 1,
  triggerCartModal = true,
  className,
  successCallback,
  tooltipText = '',
  children,
  ...props
}: Props & Record<string, any>): ReactElement {
  const addItem = useAddItems({
    include: ['line_items.physical_items.options'],
  });
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>();
  const { setQuickCartIsOpen, addingToCartStatus, setAddingToCartStatus } =
    useContext<UIContextInterface>(UIContext);

  // Add the item
  const addToCart = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    logBreadcrumb({
      category: 'AddToCart',
      message: 'Clicked add to cart button',
      level: 'info',
      data: {
        productId,
        variantId,
        quantity,
      },
    });

    let cartResponse: { data: Cart | null; error: string | null };
    try {
      setAddingToCartStatus(AddToCartStatus.Adding);
      cartResponse = await addItem({
        lineItems: [
          {
            productId,
            variantId,
            quantity,
          },
        ],
      });

      if (cartResponse.error !== null) {
        setError(true);
        if (cartResponse.error) {
          setAddingToCartStatus(AddToCartStatus.Failed);
          setErrorMessage(
            <Paragraph>
              We couldn't add that to your cart. {cartResponse.error}
            </Paragraph>
          );
        } else {
          throw new Error('Unexpected error');
        }
      } else {
        if (triggerCartModal) {
          setQuickCartIsOpen(true);
        }
      }
      setAddingToCartStatus(AddToCartStatus.Added);
      if (successCallback) successCallback();
    } catch (error) {
      setAddingToCartStatus(AddToCartStatus.Failed);
      setError(true);
      setErrorMessage(
        <Paragraph>
          We couldn't add that to your cart. If you continue to have issues,
          please contact customer service.
        </Paragraph>
      );
    }
  };

  return (
    <>
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
      <form onSubmit={addToCart}>
        <input type="hidden" value={productId} name="productId" />
        <input type="hidden" value={variantId} name="variantId" />
        <input type="hidden" value={quantity} name="quantity" />
        <input type="hidden" value={sku} name="sku" />
        <input type="hidden" value={name} name="productName" />
        <TooltipWrapper text={tooltipText}>
          <Button
            buttonStyle={buttonStyle ?? 'primary'}
            className={twJoin([className ?? '', 'gae-product-atc'])}
            type="submit"
            disabled={addingToCartStatus === AddToCartStatus.Adding || disabled}
            data-umami-event="hue-add-to-cart-clicked"
            data-umami-event-productid={productId}
            data-umami-event-variantid={variantId}
            data-umami-event-quantity={quantity}
            data-umami-event-sku={sku}
            {...props}
          >
            {children}
          </Button>
        </TooltipWrapper>
      </form>
    </>
  );
}
